import { ICreateCheckoutSessionResponseDto } from "../../../../models/IntegrationsTypes";
import WalletService, { IWallet } from "../../../../services/api/wallet-service";
import { rootContainer, TYPE } from "../../../../services/container/root";
import {isSet} from "../../../../models/model-types-methods";


interface IEventMessage {
    type: string;
    [payloadKey: string]: any;
}

interface IWalletOAuthReponse {
    createCheckoutSessionWithSetupIntentResponse: ICreateCheckoutSessionResponseDto,
    oauthPayload: {
        success: boolean,
        cancel: boolean
    }
}

class WalletSetupHandler {
    private _window: Window | null = null;
    private _intervalId: any = undefined;
    private _messageHandler?: (event: IEventMessage) => void;
    private _closeInterval = 50;
    
    private get walletService(): WalletService {
        return rootContainer.get<WalletService>(TYPE.WalletService);
    }

    public async setupWallet(wallet?: IWallet): Promise<IWallet> {
        const checkoutSessionSetupIntentResponse = await this.openCheckoutSessionWithSetupIntentPopupAndListen(wallet);
        if (checkoutSessionSetupIntentResponse.oauthPayload.cancel) {
            throw new Error('Create session with setup intent was cancelled.')
        }
        return await this.confirmCheckoutSessionWithSetupIntent(checkoutSessionSetupIntentResponse.createCheckoutSessionWithSetupIntentResponse);
    }

    private async openCheckoutSessionWithSetupIntentPopupAndListen(wallet?: IWallet): Promise<IWalletOAuthReponse> {

        if (this._messageHandler) {
            window.removeEventListener('message', this._messageHandler, false);
        }

        return new Promise(async (resolve, reject) => {
            let linkResponse: ICreateCheckoutSessionResponseDto;
            /** when we need to request the window popup url from server (integrator) */
            try {
                linkResponse = await this.walletService.createCheckoutSessionWithSetupIntent(wallet);
            }
            catch (err) {
                reject(err);
                return;
            }

            /**
             * https://developer.mozilla.org/en-US/docs/Web/API/Window/open
             *
             * null is returned if the browser fails to open the new browsing context, for example because it was
             * blocked by a browser popup blocker.
             */
            this._window = window.open(linkResponse.url, '', 'width=800,height=600,resizeable,scrollbars');

            if (!isSet(this._window)) {
                reject(new Error('Failed to open the Stripe payment method setup window'));
                return;
            }

            this._messageHandler = (event: IEventMessage) => {
                this._handleMessage(event, resolve, linkResponse);
            };

            window.addEventListener('message', this._messageHandler, false);

            this._listenToWindowClosed(reject);
        });
    }

    private async confirmCheckoutSessionWithSetupIntent(body: ICreateCheckoutSessionResponseDto): Promise<IWallet> {
        return await this.walletService.confirmCheckoutSessionWithSetupIntent(body);
    }

    private _listenToWindowClosed(reject: (reason?: any) => void) {
        /** make sure to always clear interval before starting new one */
        clearInterval(this._intervalId);

        this._intervalId = setInterval(() => {
            if (!this._window?.closed) {
                return;
            }
            clearInterval(this._intervalId);
            reject(new Error('Authorization interrupted, window was closed before finishing the process'));
        }, this._closeInterval);
    }

    private _handleMessage(
        event: IEventMessage, 
        resolve: (value: IWalletOAuthReponse) => void, 
        createCheckoutSessionWithSetupIntentResponse: ICreateCheckoutSessionResponseDto
    ) {
        if (event.data?.type !== 'OAUTH_RESPOSE') {
            return;
        }

        if (isSet(this._window)) {
            this._window?.close();
        }

        window.removeEventListener('message', this._messageHandler as (event: IEventMessage) => void, false);

        return resolve({
            createCheckoutSessionWithSetupIntentResponse,
            oauthPayload: event.data.payload,
        });
    }
}

export default WalletSetupHandler;
